import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getPaymentList } from '../services/api';
import { DataTable, FormTextField, FormDatePicker, FormSelect } from '../shared';
import moment from 'moment';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Skeleton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PaymentsIcon from '@mui/icons-material/Payments';
import { Login as LoginIcon, Logout as LogoutIcon } from '@mui/icons-material';
import PaymentCollectionDrawer from './finance/PaymentCollectionDrawer';
import BookingAuditDrawer from './booking/BookingAuditDrawer';
import DashboardReportHeader from './common/DashboardReportHeader';
import useMobileView from '../hooks/useMobileView';
import { getPaymentStatusColor, getPaymentStatusLabel } from '../utils/bookingOpsCardHelpers';
import './PaymentList.css';
import { formatHotelDate } from '../utils/dateUtils';

const PAYMENT_ACCENT = '#4ce1b6';

const createDefaultPaymentFilters = () => ({
  booking_code: '',
  invoice_number: '',
  guest: '',
  booking_payment_status: [],
  payment_status: '',
  payment_method: [],
  check_in_date: '',
  check_in_from: '',
  check_in_to: '',
});

const DEFAULT_PAYMENT_LIST_SORT = { column: 'check_in_date', direction: 'desc' };

function resolvePaymentBookingId(row) {
  const bookingId = Number(row?.booking_id);
  return Number.isInteger(bookingId) && bookingId > 0 ? bookingId : null;
}

const BOOKING_PAYMENT_FILTER_OPTIONS = [
  { value: 'full', label: 'Full Payment' },
  { value: 'partial', label: 'Partial Payment' },
  { value: 'due', label: 'Due' },
];

const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function useDebouncedTextFilters(filters, delay = 300) {
  const textKeys = useMemo(
    () => ({
      booking_code: filters.booking_code,
      invoice_number: filters.invoice_number,
      guest: filters.guest,
    }),
    [filters.booking_code, filters.invoice_number, filters.guest],
  );
  const debouncedText = useDebouncedValue(textKeys, delay);
  return useMemo(
    () => ({
      ...filters,
      ...debouncedText,
    }),
    [filters, debouncedText],
  );
}

function renderPaymentFieldSkeleton(field, skeletonBg) {
  switch (field) {
    case 'check_in_date':
      return (
        <Stack spacing={0.45}>
          <Skeleton variant="text" animation="wave" height={14} sx={{ bgcolor: skeletonBg, width: '82%' }} />
          <Skeleton variant="text" animation="wave" height={14} sx={{ bgcolor: skeletonBg, width: '74%' }} />
        </Stack>
      );
    case 'amount':
      return (
        <Stack spacing={0.45}>
          <Skeleton variant="text" animation="wave" height={14} sx={{ bgcolor: skeletonBg, width: '88%' }} />
          <Skeleton variant="text" animation="wave" height={12} sx={{ bgcolor: skeletonBg, width: '62%' }} />
        </Stack>
      );
    case 'payment_status':
      return (
        <Skeleton
          variant="rounded"
          animation="wave"
          height={26}
          width={78}
          sx={{ bgcolor: skeletonBg, borderRadius: 2 }}
        />
      );
    case 'payment_method':
      return (
        <Stack direction="row" spacing={0.75}>
          <Skeleton variant="rounded" animation="wave" height={26} width={96} sx={{ bgcolor: skeletonBg, borderRadius: 2 }} />
          <Skeleton variant="rounded" animation="wave" height={26} width={72} sx={{ bgcolor: skeletonBg, borderRadius: 2 }} />
        </Stack>
      );
    case 'actions':
      return (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Skeleton variant="circular" animation="wave" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
          <Skeleton variant="circular" animation="wave" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
        </Stack>
      );
    case 'invoice_number':
    case 'booking_code':
      return <Skeleton variant="text" animation="wave" height={20} sx={{ bgcolor: skeletonBg, width: '76%' }} />;
    case 'guest_name':
      return <Skeleton variant="text" animation="wave" height={20} sx={{ bgcolor: skeletonBg, width: '64%' }} />;
    default:
      return <Skeleton variant="text" animation="wave" height={20} sx={{ bgcolor: skeletonBg, width: '58%' }} />;
  }
}

const paymentSkeletonCell = (field) => ({ skeletonBg }) => renderPaymentFieldSkeleton(field, skeletonBg);

function PaymentMobileSkeleton({ rowCount = 6, isDark }) {
  const skeletonBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)';
  return (
    <Box>
      {Array.from({ length: rowCount }).map((_, index) => (
        <Box key={`payment-mobile-sk-${index}`}>
          <Box sx={{ p: 1.25 }}>
            {renderPaymentFieldSkeleton('amount', skeletonBg)}
            <Box sx={{ pl: 2.25, mt: 0.75 }}>{renderPaymentFieldSkeleton('check_in_date', skeletonBg)}</Box>
            <Box sx={{ pl: 2.25, mt: 0.75 }}>{renderPaymentFieldSkeleton('payment_method', skeletonBg)}</Box>
            <Box sx={{ pl: 2.25, mt: 0.75 }}>
              <Skeleton variant="text" animation="wave" height={12} sx={{ bgcolor: skeletonBg, width: '70%' }} />
            </Box>
          </Box>
          {index < rowCount - 1 ? <Divider sx={{ opacity: 0.12 }} /> : null}
        </Box>
      ))}
    </Box>
  );
}

const paymentMetaChipBase = {
  height: 26,
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'capitalize',
  '& .MuiChip-label': { px: 1 },
};

function normalizePaymentMethodKey(display) {
  const s = String(display).toLowerCase();
  if (s.includes('credit')) return 'credit_card';
  if (s.includes('debit')) return 'debit_card';
  if (/\bcash\b/.test(s) || s.trim() === 'cash') return 'cash';
  if (s.includes('bank') || s.includes('transfer') || s.includes('neft') || s.includes('rtgs'))
    return 'bank_transfer';
  if (s.includes('upi')) return 'upi';
  if (s.includes('wallet')) return 'wallet';
  if (s.includes('cheque') || s.includes('check')) return 'cheque';
  if (s.includes('other')) return 'other';
  const snake = s.replace(/\s+/g, '_');
  const known = ['cash', 'credit_card', 'debit_card', 'bank_transfer', 'upi', 'wallet', 'cheque', 'other'];
  if (known.includes(snake)) return snake;
  return 'other';
}

function chipTintSx(theme, mainColor) {
  return {
    ...paymentMetaChipBase,
    color: mainColor,
    borderColor: alpha(mainColor, 0.55),
    bgcolor: alpha(mainColor, theme.palette.mode === 'dark' ? 0.2 : 0.1),
  };
}

function paymentMethodChipSx(theme, methodKey) {
  switch (methodKey) {
    case 'cash':
      return chipTintSx(theme, theme.palette.success.main);
    case 'credit_card':
      return chipTintSx(theme, theme.palette.primary.main);
    case 'debit_card':
      return chipTintSx(theme, theme.palette.info.main);
    case 'bank_transfer':
      return chipTintSx(theme, '#8e7dff');
    case 'upi':
      return chipTintSx(theme, '#7b1fa2');
    case 'wallet':
      return chipTintSx(theme, theme.palette.warning.main);
    case 'cheque':
      return chipTintSx(theme, '#e65100');
    case 'other':
    default:
      return {
        ...paymentMetaChipBase,
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        bgcolor: alpha(theme.palette.action.selected, theme.palette.mode === 'dark' ? 0.15 : 0.08),
      };
  }
}

function formatPaymentAmount(amount, currency = 'INR') {
  const value = amount ? parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00';
  if (currency === 'INR') return `₹${value}`;
  return `${currency} ${value}`;
}

function formatStayDate(value) {
  if (!value || value === '—') return '—';
  const parsed = moment(value);
  return parsed.isValid() ? formatHotelDate(value) : '—';
}

function StayLine({ Icon, iconColor, label, date }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ lineHeight: 1.2 }}>
      <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0, opacity: 0.92 }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', minWidth: 22 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
        {date}
      </Typography>
    </Stack>
  );
}

function PaymentStayCell({ row }) {
  const nights = Number(row?.nights || 0);
  const hasNights = Number.isFinite(nights) && nights > 0;
  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 155 }}>
      <StayLine
        Icon={LoginIcon}
        iconColor="#4ce1b6"
        label="In"
        date={formatStayDate(row?.check_in_date || row?.booking_date)}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StayLine
          Icon={LogoutIcon}
          iconColor="#60a5fa"
          label="Out"
          date={formatStayDate(row?.check_out_date)}
        />
        {hasNights ? (
          <Typography
            variant="caption"
            sx={{
              ml: 0.25,
              color: '#4ce1b6',
              fontWeight: 800,
              fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}
          >
            · {nights}N
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function CheckboxMultiFilter({
  label,
  emptyLabel,
  options,
  value = [],
  onChange,
  isDark,
  minWidth = 150,
}) {
  const selected = Array.isArray(value) ? value : [];
  const selectedLabel = useMemo(() => {
    if (!selected.length) return emptyLabel;
    if (selected.length === options.length) return emptyLabel;
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options, emptyLabel]);

  return (
    <FormControl size="small" sx={{ minWidth, width: '100%' }}>
      <InputLabel shrink sx={{ color: isDark ? '#999' : '#757575' }}>
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
        }}
        label={label}
        renderValue={() => selectedLabel}
        sx={{
          color: isDark ? '#ddd' : '#212121',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#333246' : '#e0e0e0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#555' : '#999' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PAYMENT_ACCENT },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              '& .MuiMenuItem-root': {
                color: isDark ? '#ddd' : '#212121',
                '&:hover': { backgroundColor: isDark ? '#38373f' : '#f5f5f5' },
                '&.Mui-selected': {
                  backgroundColor: isDark ? '#38373f' : alpha(PAYMENT_ACCENT, 0.12),
                  '&:hover': { backgroundColor: isDark ? '#444350' : alpha(PAYMENT_ACCENT, 0.18) },
                },
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              checked={selected.includes(option.value)}
              sx={{
                color: isDark ? '#999' : '#757575',
                '&.Mui-checked': { color: PAYMENT_ACCENT },
              }}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function BookingPaymentMultiFilter({ value = [], onChange, isDark }) {
  return (
    <CheckboxMultiFilter
      label="Payment"
      emptyLabel="All"
      options={BOOKING_PAYMENT_FILTER_OPTIONS}
      value={value}
      onChange={onChange}
      isDark={isDark}
    />
  );
}

function PaymentMethodMultiFilter({ value = [], onChange, isDark }) {
  return (
    <CheckboxMultiFilter
      label="Payment Method"
      emptyLabel="All"
      options={PAYMENT_METHOD_FILTER_OPTIONS}
      value={value}
      onChange={onChange}
      isDark={isDark}
      minWidth={160}
    />
  );
}

function buildPaymentListParams(filters, pagination, sortConfig) {
  const sortColumn = sortConfig.column || 'check_in_date';
  const sortDirection = sortConfig.direction || 'desc';
  const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;

  const params = {
    page: pagination.page + 1,
    per_page: pagination.perPage,
    sort: sortParam,
    group_by_invoice: 1,
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'booking_payment_status' || key === 'payment_method') {
      if (Array.isArray(value) && value.length > 0) {
        params[key] = value.join(',');
      }
      return;
    }
    if (key === 'check_in_date' || key === 'check_in_from' || key === 'check_in_to') {
      return;
    }
    if (value !== '' && value != null) {
      params[key] = value;
    }
  });

  if (filters.check_in_date) {
    params.booking_date_from = filters.check_in_date;
    params.booking_date_to = filters.check_in_date;
  } else {
    if (filters.check_in_from) params.booking_date_from = filters.check_in_from;
    if (filters.check_in_to) params.booking_date_to = filters.check_in_to;
  }

  return params;
}

function resolvePaymentMethods(row) {
  if (Array.isArray(row.payment_methods) && row.payment_methods.length > 0) {
    return row.payment_methods;
  }
  if (row.payment_method && row.payment_method !== '—' && row.payment_method !== 'Multiple') {
    return [{ label: row.payment_method, value: row.payment_method }];
  }
  return [];
}

function resolveBookingPaymentTotals(row) {
  const summary = row.booking_payment_summary || {};
  const totalAmount = Number(
    row.total_amount ?? summary.total_amount ?? 0,
  );
  const totalPaid = Number(
    row.total_paid ?? summary.total_paid ?? row.amount ?? 0,
  );
  const dueAmount = Number(
    row.due_amount ?? summary.remaining_balance ?? Math.max(0, totalAmount - totalPaid),
  );
  return { totalAmount, totalPaid, dueAmount };
}

function PaymentAmountTypeCell({ row }) {
  const { totalAmount, totalPaid, dueAmount } = resolveBookingPaymentTotals(row);
  const status = row.booking_balance_status;
  const statusLabel = row.booking_balance_label || getPaymentStatusLabel(status);
  const statusColor = getPaymentStatusColor(status);
  const showDue = status === 'partial_payment' || status === 'due';

  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 168 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
        <PaymentsIcon sx={{ fontSize: 14, color: statusColor, flexShrink: 0, opacity: 0.9 }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {formatPaymentAmount(totalAmount, row.currency)}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.62rem',
            whiteSpace: 'nowrap',
          }}
        >
          · {statusLabel}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.25, pl: 2.25 }}>
        <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>
          Paid {formatPaymentAmount(totalPaid, row.currency)}
        </Box>
        {showDue ? (
          <>
            <Box component="span" sx={{ mx: 0.5, opacity: 0.5 }}>
              ·
            </Box>
            <Box component="span" sx={{ color: '#ff7043', fontWeight: 700 }}>
              Due {formatPaymentAmount(dueAmount, row.currency)}
            </Box>
          </>
        ) : null}
      </Typography>
    </Stack>
  );
}

function PaymentMethodsCell({ row, theme }) {
  const methods = resolvePaymentMethods(row);
  if (!methods.length) return '—';

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 220 }}>
      {methods.map((method) => {
        const methodKey = normalizePaymentMethodKey(method.label || method.value);
        return (
          <Chip
            key={`${method.value}-${method.label}`}
            label={method.label}
            size="small"
            variant="outlined"
            sx={paymentMethodChipSx(theme, methodKey)}
          />
        );
      })}
    </Stack>
  );
}

function PaymentStatusCell({ row, theme }) {
  const label = row.collection_status_label || row.payment_status;
  if (!label || label === '—') return '—';
  const statusKey = String(row.collection_status_value || row.payment_status_value || 'unknown').toLowerCase();

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={paymentStatusChipSx(theme, statusKey)}
    />
  );
}

function paymentStatusChipSx(theme, statusValue) {
  switch (statusValue) {
    case 'pending':
      return chipTintSx(theme, theme.palette.warning.main);
    case 'success':
      return chipTintSx(theme, theme.palette.success.main);
    case 'failed':
      return chipTintSx(theme, theme.palette.error.main);
    case 'mixed':
      return chipTintSx(theme, theme.palette.warning.main);
    case 'reversed':
      return {
        ...paymentMetaChipBase,
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        bgcolor: alpha(theme.palette.action.selected, theme.palette.mode === 'dark' ? 0.15 : 0.08),
      };
    default:
      return {
        ...paymentMetaChipBase,
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        bgcolor: alpha(theme.palette.action.selected, theme.palette.mode === 'dark' ? 0.15 : 0.08),
      };
  }
}

function PaymentMobileList({
  data,
  loading,
  emptyMessage,
  onOpenCollectionDrawer,
  theme,
}) {
  if (loading && data.length === 0) {
    return <PaymentMobileSkeleton rowCount={6} isDark={theme.palette.mode === 'dark'} />;
  }

  if (!data.length) {
    return (
      <Typography variant="body2" sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {data.map((row, index) => {
        const subtitle = [row.guest_name, row.invoice_number]
          .filter((part) => part && part !== '—')
          .join(' · ');

        return (
          <Box key={row.id || index}>
            <Box
              component="button"
              type="button"
              onClick={() => onOpenCollectionDrawer(row)}
              sx={{
                width: '100%',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                p: 1.25,
                m: 0,
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              <PaymentAmountTypeCell row={row} />
              <Box sx={{ pl: 2.25, mt: 0.5 }}>
                <PaymentStayCell row={row} />
              </Box>
              <Box sx={{ pl: 2.25, mt: 0.5 }}>
                <PaymentMethodsCell row={row} theme={theme} />
              </Box>
              {row.collection_status_label && row.collection_status_label !== '—' ? (
                <Box sx={{ pl: 2.25, mt: 0.5 }}>
                  <PaymentStatusCell row={row} theme={theme} />
                </Box>
              ) : null}
              {subtitle ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.35,
                    pl: 2.25,
                    color: 'text.secondary',
                    fontSize: '0.68rem',
                    lineHeight: 1.25,
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {index < data.length - 1 ? <Divider sx={{ opacity: 0.12 }} /> : null}
          </Box>
        );
      })}
    </Box>
  );
}

const PaymentList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isCompact } = useMobileView();
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [toolbarResetKey, setToolbarResetKey] = useState(0);
  const [filters, setFilters] = useState(() => createDefaultPaymentFilters());
  const [sortConfig, setSortConfig] = useState({ ...DEFAULT_PAYMENT_LIST_SORT });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const queryFilters = useDebouncedTextFilters(filters);
  const [collectionDrawerOpen, setCollectionDrawerOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditBooking, setAuditBooking] = useState(null);

  // Fetch payment data
  const fetchPaymentData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = buildPaymentListParams(queryFilters, pagination, sortConfig);

      const response = await getPaymentList(params);
      
      if (response && response.Data) {
        setPaymentData(response.Data);
        setPagination(prev => ({
          ...prev,
          totalRecords: response.total_records || 0,
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  }, [queryFilters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'check_in_date' && value) {
        next.check_in_from = '';
        next.check_in_to = '';
      }
      return next;
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleDateRangeChange = useCallback((nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setFilters((prev) => ({
      ...prev,
      check_in_date: '',
      check_in_from: nextStart ? nextStart.format('YYYY-MM-DD') : '',
      check_in_to: nextEnd ? nextEnd.format('YYYY-MM-DD') : '',
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefreshAndReset = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setFilters(createDefaultPaymentFilters());
    setSortConfig({ ...DEFAULT_PAYMENT_LIST_SORT });
    setPagination((prev) => ({ ...prev, page: 0 }));
    setToolbarResetKey((key) => key + 1);
  }, []);

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, page: 0 }));
  };

  const handleOpenCollectionDrawer = (row) => {
    setSelectedPayment(row);
    setCollectionDrawerOpen(true);
  };

  const handleOpenAuditDrawer = (row) => {
    const bookingId = resolvePaymentBookingId(row);
    if (!bookingId) return;
    setAuditBooking({
      bookingId,
      bookingCode: row.booking_code && row.booking_code !== '—' ? row.booking_code : null,
    });
    setAuditDrawerOpen(true);
  };

  const columns = [
    {
      field: 'invoice_number',
      label: 'Invoice ID',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('invoice_number'),
      render: (value, row) => {
        if (!value || value === '—') return '—';
        return (
          <Box
            component="span"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      field: 'booking_code',
      label: 'Booking Code',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('booking_code'),
      render: (value, row) => {
        if (value && value !== '—' && row.booking_id) {
          return (
            <Link
              to={`/booking/detail/${row.booking_id}/`}
              onClick={(e) => e.stopPropagation()}
              style={{ color: theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}
            >
              {value}
            </Link>
          );
        }
        return value || '—';
      },
    },
    {
      field: 'guest_name',
      label: 'Guest Name',
      sortable: false,
      renderSkeleton: paymentSkeletonCell('guest_name'),
    },
    {
      field: 'check_in_date',
      label: 'Stay',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('check_in_date'),
      render: (_value, row) => <PaymentStayCell row={row} />,
    },
    {
      field: 'amount',
      label: 'Payment',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('amount'),
      render: (_value, row) => <PaymentAmountTypeCell row={row} />,
    },
    {
      field: 'payment_status',
      label: 'Payment Status',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('payment_status'),
      render: (_value, row) => <PaymentStatusCell row={row} theme={theme} />,
    },
    {
      field: 'payment_method',
      label: 'Payment Method',
      sortable: true,
      renderSkeleton: paymentSkeletonCell('payment_method'),
      render: (_value, row) => <PaymentMethodsCell row={row} theme={theme} />,
    },
    {
      field: 'actions',
      label: 'Action',
      sortable: false,
      align: 'center',
      renderSkeleton: paymentSkeletonCell('actions'),
      render: (_value, row) => {
        const bookingId = resolvePaymentBookingId(row);
        const iconButtonSx = {
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
          },
        };

        return (
          <Stack
            direction="row"
            spacing={0.25}
            justifyContent="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="View payment collection details">
              <IconButton
                size="small"
                aria-label="View payment collection details"
                onClick={() => handleOpenCollectionDrawer(row)}
                sx={iconButtonSx}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={bookingId ? 'View booking audit log' : 'Booking audit unavailable'}>
              <span>
                <IconButton
                  size="small"
                  aria-label="View booking audit log"
                  disabled={!bookingId}
                  onClick={() => handleOpenAuditDrawer(row)}
                  sx={iconButtonSx}
                >
                  <FactCheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  // Create filter row cells
  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'booking_code':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Booking Code"
            value={filters.booking_code}
            onChange={(e) => handleFilterChange('booking_code', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'invoice_number':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Invoice ID"
            value={filters.invoice_number}
            onChange={(e) => handleFilterChange('invoice_number', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'guest_name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Guest Name/Email"
            value={filters.guest}
            onChange={(e) => handleFilterChange('guest', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'check_in_date':
        return (
          <FormDatePicker
            key={column.field}
            label="Stay Date"
            value={filters.check_in_date ? moment(filters.check_in_date) : null}
            onChange={(newValue) => handleFilterChange('check_in_date', newValue ? newValue.format('YYYY-MM-DD') : '')}
            sx={{ minWidth: 140 }}
          />
        );
      case 'amount':
        return (
          <Box key={column.field} onClick={(e) => e.stopPropagation()}>
            <BookingPaymentMultiFilter
              value={filters.booking_payment_status}
              onChange={(value) => handleFilterChange('booking_payment_status', value)}
              isDark={isDark}
            />
          </Box>
        );
      case 'payment_status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.payment_status}
            onChange={(e) => handleFilterChange('payment_status', e.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="reversed">Reversed</MenuItem>
          </FormSelect>
        );
      case 'payment_method':
        return (
          <Box key={column.field} onClick={(e) => e.stopPropagation()}>
            <PaymentMethodMultiFilter
              value={filters.payment_method}
              onChange={(value) => handleFilterChange('payment_method', value)}
              isDark={isDark}
            />
          </Box>
        );
      default:
        return <div key={column.field}></div>;
    }
  });

  return (
    <Box className="payment-list-container">
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}

      {isCompact ? (
        <>
          <Box sx={{ mb: 2 }}>
            <DashboardReportHeader
              key={toolbarResetKey}
              actionsOnly
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={handleDateRangeChange}
              onRefresh={handleRefreshAndReset}
              loading={loading}
              showPdf={false}
              themeMode={isDark ? 'dark' : 'light'}
              accentColor={PAYMENT_ACCENT}
              allowAllPreset
              initialPreset="all"
            />
          </Box>
          <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <BookingPaymentMultiFilter
              value={filters.booking_payment_status}
              onChange={(value) => handleFilterChange('booking_payment_status', value)}
              isDark={isDark}
            />
          </Stack>
          <PaymentMobileList
            data={paymentData}
            loading={loading}
            emptyMessage="No payments found matching the selected filters."
            onOpenCollectionDrawer={handleOpenCollectionDrawer}
            theme={theme}
          />
          {pagination.totalRecords > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {pagination.page * pagination.perPage + 1}–
                {Math.min((pagination.page + 1) * pagination.perPage, pagination.totalRecords)} of {pagination.totalRecords}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loading || pagination.page === 0}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    loading
                    || (pagination.page + 1) * pagination.perPage >= pagination.totalRecords
                  }
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : null}
        </>
      ) : (
        <DataTable
          columns={columns}
          data={paymentData}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onSortChange={handleSortChange}
          sortConfig={sortConfig}
          emptyMessage="No payments found matching the selected filters."
          stickyHeader={true}
          filterRow={filterRowCells}
          skeletonRowCount={pagination.perPage}
          onRowClick={handleOpenCollectionDrawer}
          optimizeScroll
          toolbarRight={
            <DashboardReportHeader
              key={toolbarResetKey}
              actionsOnly
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={handleDateRangeChange}
              onRefresh={handleRefreshAndReset}
              loading={loading}
              showPdf={false}
              themeMode={isDark ? 'dark' : 'light'}
              accentColor={PAYMENT_ACCENT}
              allowAllPreset
              initialPreset="all"
            />
          }
        />
      )}

      <PaymentCollectionDrawer
        open={collectionDrawerOpen}
        onClose={() => {
          setCollectionDrawerOpen(false);
          setSelectedPayment(null);
        }}
        paymentRow={selectedPayment}
        onPaymentsChanged={fetchPaymentData}
      />

      <BookingAuditDrawer
        open={auditDrawerOpen}
        onClose={() => {
          setAuditDrawerOpen(false);
          setAuditBooking(null);
        }}
        bookingId={auditBooking?.bookingId}
        bookingCode={auditBooking?.bookingCode}
        isDark={isDark}
      />
    </Box>
  );
};

export default PaymentList;

